import { FunctionComponent, useCallback, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import SvgFragment from '../fragments/SvgFragment'
import { styles } from '../styles/themes/style'
import {
    dismissWebUpdateBanner,
    getWebUpdateBannerKey,
    shouldShowWebUpdateBanner,
} from '../utils/webUpdateBanner'

const WebUpdateBanner: FunctionComponent = () => {
    const bannerKey = getWebUpdateBannerKey()
    const [visible, setVisible] = useState(() => shouldShowWebUpdateBanner())

    const hide = useCallback(() => {
        if (bannerKey) dismissWebUpdateBanner(bannerKey)
        setVisible(false)
    }, [bannerKey])

    const refresh = useCallback(() => {
        if (bannerKey) dismissWebUpdateBanner(bannerKey)
        setVisible(false)
        window.location.reload()
    }, [bannerKey])

    if (!visible || !bannerKey) return null

    return (
        <Card role="dialog" aria-labelledby="web-update-banner-title" aria-describedby="web-update-banner-desc">
            <Header>
                <TitleRow>
                    <IconWrap aria-hidden>
                        <SvgFragment type="sparkle" />
                    </IconWrap>
                    <Title id="web-update-banner-title">LeadDelta just got better</Title>
                </TitleRow>
                <CloseButton type="button" aria-label="Close" onClick={hide}>
                    <SvgFragment type="closeNotification" />
                </CloseButton>
            </Header>
            <Description id="web-update-banner-desc">
                Refresh now to get the latest features and improvements.
            </Description>
            <Actions>
                <PrimaryButton type="button" onClick={refresh}>
                    Refresh now
                </PrimaryButton>
                <SecondaryButton type="button" onClick={hide}>
                    Dismiss
                </SecondaryButton>
            </Actions>
        </Card>
    )
}

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const Card = styled.div`
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 10000001;
    width: min(360px, calc(100vw - 32px));
    padding: 16px;
    border-radius: 16px;
    background:
        radial-gradient(circle at top left, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 42%),
        linear-gradient(135deg, #ffffff 0%, #f5f7ff 48%, #edf9ff 100%);
    border: 1px solid rgba(100, 103, 242, 0.18);
    box-shadow: 0 18px 42px rgba(30, 41, 59, 0.18), 0 6px 16px rgba(89, 100, 255, 0.12);
    animation: ${slideIn} 280ms ease-out;
    font-family: NunitoSans, sans-serif;

    &::after {
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 17px;
        padding: 1px;
        background: linear-gradient(
            115deg,
            rgba(100, 103, 242, 0),
            rgba(100, 103, 242, 0.9),
            rgba(48, 215, 186, 0.75),
            rgba(100, 103, 242, 0)
        );
        pointer-events: none;
        opacity: 0.7;
        -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`

const IconWrap = styled.div`
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${styles.colors.primary600};
    background: rgba(89, 100, 255, 0.1);

    & .svg {
        width: 16px;
        height: 16px;
    }

    & .path {
        stroke: ${styles.colors.primary600};
        fill: none;
    }
`

const Title = styled.h2`
    margin: 0;
    font-size: 15px;
    font-weight: 800;
    line-height: 20px;
    color: ${styles.colors.black600};
`

const CloseButton = styled.button`
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    margin: -2px -2px 0 0;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: ${styles.colors.black300};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(140, 150, 173, 0.12);
        color: ${styles.colors.black600};
    }
`

const Description = styled.p`
    margin: 8px 0 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: ${styles.colors.black400};
`

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
`

const PrimaryButton = styled.button`
    flex: 1;
    height: 36px;
    padding: 0 14px;
    border: none;
    border-radius: 10px;
    background: ${styles.colors.primary600};
    color: ${styles.colors.white};
    font-family: inherit;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background: ${styles.colors.primary400};
    }
`

const SecondaryButton = styled.button`
    flex: 1;
    height: 36px;
    padding: 0 14px;
    border: 1px solid ${styles.colors.black200};
    border-radius: 10px;
    background: ${styles.colors.white};
    color: ${styles.colors.black600};
    font-family: inherit;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease;

    &:hover {
        border-color: ${styles.colors.primary600};
        color: ${styles.colors.primary600};
    }
`

export default WebUpdateBanner
